import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "@/lib/i18n";

const DEFAULT_IMAGE = "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1200&auto=format&fit=crop";

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href, extra = {}) {
  const selector = extra.hreflang ? `link[rel="${rel}"][hreflang="${extra.hreflang}"]` : `link[rel="${rel}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (extra.hreflang) el.setAttribute("hreflang", extra.hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Per-page SEO manager: title, description, canonical, Open Graph, Twitter, hreflang.
 * Usage: <Seo title="..." description="..." /> at the top of each page.
 */
export function Seo({ title, description, image = DEFAULT_IMAGE, type = "website", noindex = false }) {
  const { lang } = useLang();
  const { pathname } = useLocation();

  useEffect(() => {
    const origin = window.location.origin;
    const url = `${origin}${pathname}`;
    const fullTitle = title ? `${title} | II Eleven Gruppe` : "II Eleven Gruppe \u2013 Family Office, Real Estate, Maklerservice & Development";

    document.title = fullTitle;
    document.documentElement.setAttribute("lang", lang === "en" ? "en" : "de");

    upsertMeta("name", "description", description || "");
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");

    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description || "");
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:site_name", "Eleven Family Office");
    upsertMeta("property", "og:locale", lang === "en" ? "en_US" : "de_DE");
    upsertMeta("property", "og:locale:alternate", lang === "en" ? "de_DE" : "en_US");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description || "");
    upsertMeta("name", "twitter:image", image);

    upsertLink("canonical", url);
    upsertLink("alternate", url, { hreflang: "de" });
    upsertLink("alternate", url, { hreflang: "en" });
    upsertLink("alternate", url, { hreflang: "x-default" });
  }, [title, description, image, type, noindex, lang, pathname]);

  return null;
}
